/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import { useEffect, useState } from 'react'
import { CustomTable } from 'common/CustomTable/CustomTable';
import { Container, Paper } from '@mui/material'
import { TableTitle } from '../../common'
import { useExpanded } from '../../components/layouts/ProtectedLayout';
import { useConfigService, useQueryAgencyManagement, useSetConfig } from 'hooks';
import { AddNewAgencyModal, EditAgencyDrawer } from './components/index';
import { pageStyles } from 'customStyles/common';
import { useTableStore } from 'state/TableStore';
import { extractPageName } from 'common/CustomTable/helpers';
import { useLocation } from 'react-router-dom';
import { useUserStore } from 'state';
import { TableNames } from 'customEnums/index';
import { useAgencyManagementStore } from './helpers/AgencyManagementStore';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export const AgencyManagement = () => {
  const { expanded } = useExpanded();
  const userConfig = useUserStore(state => state.userConfig)
  const { usersPermissions } = userConfig
  const canAddAgencies = usersPermissions?.addNewAgencies
  const canViewAgencies = usersPermissions?.["viewAgencies"] || false
  const editExistingAgencies = usersPermissions?.editExistingAgencies
  const [editAgency, setEditAgency] = useState<any>()
  const setHeaderButtonConfig = useTableStore(state => state.setHeaderButtonConfig)
  const searchParams = useTableStore((state) => state.searchParams)
  const isPost = useTableStore((state) => state.isPost)
  const isDelete = useTableStore((state) => state.isDelete)
  const configFile = useTableStore((state) => state.configFile)
  const configBody = useTableStore((state) => state.configBody)
  useConfigService(configFile, configBody, true, isPost, isDelete);
  const tableConfig = useTableStore(state => state.tableConfig)
  const locationObj = useLocation()
  const pageName = extractPageName(locationObj.pathname)
  const setPageName = useTableStore(state => state.setPageName)
  const setSearchParams = useTableStore((state) => state.setSearchParams)
  const setSimpleSearchRan = useTableStore((state) => state.setSimpleSearchRan);

  // agency management state:
  const newAgencyForm = useAgencyManagementStore(state => state.newAgencyForm);
  const tableFilters = useAgencyManagementStore(state => state.tableFilters);
  const openModal = useAgencyManagementStore(state => state.openModal);
  const setOpenModal = useAgencyManagementStore(state => state.setOpenModal);
  const newAgencyInfo = useAgencyManagementStore(state => state.newAgencyInfo);
  const setNewAgencyInfo = useAgencyManagementStore(state => state.setNewAgencyInfo);
  const openAgencyEdit = useAgencyManagementStore(state => state.openAgencyEdit);
  const setOpenAgencyEdit = useAgencyManagementStore(state => state.setOpenAgencyEdit);
  const setSelectedRowText = useAgencyManagementStore(state => state.setSelectedRowText);
  const setUpdatedAgency = useAgencyManagementStore(state => state.setUpdatedAgency);
  const updatedAgency = useAgencyManagementStore(state => state.updatedAgency);
  const setAddedAgency = useAgencyManagementStore(state => state.setAddedAgency);
  const addedAgency = useAgencyManagementStore(state => state.addedAgency);
  const setError = useAgencyManagementStore(state => state.setError);
  const setOriExists = useAgencyManagementStore(state => state.setOriExists);
  const oriExists = useAgencyManagementStore(state => state.oriExists);
  const setRfpExists = useAgencyManagementStore(state => state.setRfpExists);
  const rfpExists = useAgencyManagementStore(state => state.rfpExists);
  const setFieldError = useAgencyManagementStore(state => state.setFieldError);
  const setTouchFields = useAgencyManagementStore(state => state.setTouchFields);
  const setAlreadyExistError = useAgencyManagementStore(state => state.setAlreadyExistError);
  const setSearched = useAgencyManagementStore(state => state.setSearched);
  const searched = useAgencyManagementStore(state => state.searched);
  const setValue = useAgencyManagementStore(state => state.setValue);
  const value = useAgencyManagementStore(state => state.value);
  const data = useAgencyManagementStore(state => state.data);
  const setData = useAgencyManagementStore(state => state.setData);

  const { tableHeaderCells, page, rowsPerPage } = useSetConfig(pageName, { page: 1, rowsPerPage: 25 }, TableNames.AGENCY_MANAGEMENT, {})

  const {
    mappedData,
    totalRecords,
    loading }: any = useQueryAgencyManagement(
      rowsPerPage,
      page,
      tableFilters,
      searchParams)

  useEffect(() => {
    setPageName('agency-management');
    console.log('AgencyManagement mounting - clearing search params');
    setSearchParams([]);
    setSimpleSearchRan(false);
  }, [setPageName, setSearchParams, setSimpleSearchRan]);

  useEffect(() => {
    setData(mappedData);
  }, [mappedData, mappedData?.length, setData]);

  useEffect(() => {
    let oriExistsTemp = false;
    let rfpExistsTemp = false;

    if (data?.length) {
      data.forEach((agency: any) => {
        if (agency?.ori?.toLowerCase() === newAgencyInfo?.["ori"]?.toLowerCase()) {
          oriExistsTemp = true;
        }
        if (agency?.reasonFingerPrinted?.toLowerCase() === newAgencyInfo?.["reasonFingerPrinted"]?.toLowerCase()) {
          rfpExistsTemp = true;
        }
      });
    }


    setOriExists(oriExistsTemp);
    setRfpExists(rfpExistsTemp);

    return () => {
      setOriExists(false);
      setRfpExists(false);
    };
  }, [newAgencyInfo, data?.ori, data?.reasonFingerPrinted, setOriExists, setRfpExists, data]);

  useEffect(() => {
    if (oriExists && rfpExists) {
      setAlreadyExistError('Agency ORI/RFP combination already exists');
    } else {
      setAlreadyExistError('');
    }
  }, [oriExists, rfpExists, setAlreadyExistError]);

  const resetAgencyForm = (form: any) => {
    Object.keys(form).forEach(key => {
      form[key] = false
    })
  }

  const handleCancel = () => {
    setNewAgencyInfo({});
    setOpenModal(false);
    setOpenAgencyEdit(false);
    setError('');
    setOriExists(false);
    setRfpExists(false);
    setAlreadyExistError('');
    setFieldError(false);
    resetAgencyForm(newAgencyForm)
  }

  useEffect(() => {
    const headerButtonConfig = {
      handler: () => setOpenModal(true),
      headerButtonIcon: null,
      buttonLabel: "",
      disabled: !canAddAgencies,
      text: "ADD AGENCY",
      displayButton: canAddAgencies
    }
    setHeaderButtonConfig(headerButtonConfig)
  })

  const handleTextFieldError = (e: any, fieldName: any) => {
    if (e.target.value === '' && fieldName !== 'secondaryOri' && fieldName !== 'agencyEmail') {
      setTouchFields({
        [fieldName]: true
      })
      setFieldError(true);
      setError('This is a required field.');
      setAlreadyExistError('');
    }
  }

  useEffect(() => {
    if (updatedAgency || addedAgency) {
      setUpdatedAgency(false);
      setAddedAgency(false);
    }
  }, [updatedAgency, addedAgency, setUpdatedAgency, setAddedAgency])

  const handleEditAgency = ((e: any, row: any) => {
    e.stopPropagation()
    setOpenAgencyEdit(true)
    setSelectedRowText(row)
    setEditAgency(() => deepClone(row))
  })

  if (!canViewAgencies) {
    return (
      <Paper elevation={0} sx={{ ...pageStyles.pagePaperStyle, minWidth: `calc(100vw - ${expanded ? "256px" : "64px"})` }}>
        <Container sx={{ mt: 4, textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page. Administrative functions are restricted to administrators only.</p>
        </Container>
      </Paper>
    );
  }
  return (
    <>
      {

        <Paper elevation={0} sx={{ ...pageStyles.pagePaperStyle, minWidth: `calc(100vw - ${expanded ? "256px" : "64px"})` }}>
          <TableTitle name="Agency Management" />
          {
            usersPermissions?.editExistingAgencies &&
            <EditAgencyDrawer
              tableHeaderCells={tableHeaderCells}
              handleTextFieldError={handleTextFieldError}
              handleCancel={handleCancel}
              currentAgency={editAgency}
              loading={loading}
            />
          }
          {
            usersPermissions?.addNewAgencies &&
            <AddNewAgencyModal
              tableHeaderCells={tableHeaderCells}
              handleTextFieldError={handleTextFieldError}
              handleCancel={handleCancel}
            />
          }
          {
            tableHeaderCells && (
              <CustomTable
                //Required props
                tableConfig={tableConfig}
                tableName="agency-management"
                loading={loading}
                rows={data}
                totalRecords={totalRecords}
                currentPage={page}
                pageSize={rowsPerPage}
                searched={searched}
                value={value}
                setValue={setValue}
                setSearched={setSearched}
                //Per page props        
                defaultSortColumn="ori"
                headerButtonAriaLabel='ADD AGENCY'
                headerButtonText="ADD AGENCY"
                rowClickHandler={editExistingAgencies ? handleEditAgency : () => { }}
                openAgencyEdit={openAgencyEdit}
                openModalBlur={openModal}
              />
            )
          }

        </Paper>
      }
    </>

  )
}
